/**
 * OpenMed/privacy-filter-multilingual emits 54 fine-grained PII categories.
 * Our public API contract uses 8 internal categories (matching the original
 * openai/privacy-filter label set for backwards-compat with existing
 * consumers and frontend marker UI).
 *
 * This module downsamples 54 → 8. Labels with no mapping (AGE, GENDER,
 * AMOUNT, etc.) are returned as null — they are not PII per our v1 schema
 * and get dropped by the api before reaching the user-facing markers.
 *
 * Mapping is included in eval reports for reproducibility — bump
 * MAPPING_VERSION on any change to the table below.
 *
 * Source for the original 54-cat list: OpenMed model card on HuggingFace
 * (https://huggingface.co/OpenMed/privacy-filter-multilingual). Last
 * synced 2026-05-22 against openmed package version 1.5.1.
 */

export const MAPPING_VERSION = "openmed-2026-05";

export type InternalCategory =
  | "private_person"
  | "private_email"
  | "private_phone"
  | "private_address"
  | "private_date"
  | "private_url"
  | "account_number"
  | "secret";

const MAP: Record<string, InternalCategory> = {
  // Identity (names + handles that identify a specific person)
  FIRSTNAME: "private_person",
  MIDDLENAME: "private_person",
  LASTNAME: "private_person",
  PREFIX: "private_person",
  USERNAME: "private_person",

  // Contact
  EMAIL: "private_email",
  PHONE: "private_phone",
  URL: "private_url",

  // Address
  STREET: "private_address",
  BUILDINGNUMBER: "private_address",
  SECONDARYADDRESS: "private_address",
  CITY: "private_address",
  COUNTY: "private_address",
  STATE: "private_address",
  ZIPCODE: "private_address",
  GPSCOORDINATES: "private_address",

  // Dates & time
  DATE: "private_date",
  DATEOFBIRTH: "private_date",
  TIME: "private_date",

  // Government IDs — semantically closest to an identifier number
  SSN: "account_number",

  // Financial identifiers (the account-number family)
  ACCOUNTNAME: "account_number",
  BANKACCOUNT: "account_number",
  IBAN: "account_number",
  BIC: "account_number",
  CREDITCARD: "account_number",
  MASKEDNUMBER: "account_number",

  // Financial secrets (high-sensitivity)
  CVV: "secret",
  PIN: "secret",

  // Crypto addresses — treat as account identifiers
  BITCOINADDRESS: "account_number",
  ETHEREUMADDRESS: "account_number",
  LITECOINADDRESS: "account_number",

  // Vehicle identifiers
  VIN: "account_number",
  VRM: "account_number",

  // Digital identifiers
  IPADDRESS: "private_url",
  MACADDRESS: "account_number",
  IMEI: "account_number",

  // Auth
  PASSWORD: "secret",
};

/**
 * Map a raw OpenMed label to our 8 internal categories.
 * Returns null for labels that are not PII per our v1 schema
 * (e.g. AGE, GENDER, AMOUNT, ORDINALDIRECTION, USERAGENT, OCCUPATION,
 * JOBTITLE, JOBDEPARTMENT, ORGANIZATION, EYECOLOR, HEIGHT, SEX,
 * CREDITCARDISSUER, CURRENCY*).
 */
export function mapModelLabel(label: string): InternalCategory | null {
  if (!label) return null;
  return MAP[label] ?? null;
}

export const INTERNAL_CATEGORIES = [
  "private_person",
  "private_email",
  "private_phone",
  "private_address",
  "private_date",
  "private_url",
  "account_number",
  "secret",
] as const satisfies readonly InternalCategory[];

export const DISPLAY_CATEGORIES = [
  "PERSON",
  "EMAIL",
  "PHONE",
  "ADDRESS",
  "DATE",
  "URL",
  "ACCOUNT",
  "SECRET",
] as const;
export type DisplayCategory = (typeof DISPLAY_CATEGORIES)[number];

export const INTERNAL_TO_DISPLAY: Record<InternalCategory, DisplayCategory> = {
  private_person: "PERSON",
  private_email: "EMAIL",
  private_phone: "PHONE",
  private_address: "ADDRESS",
  private_date: "DATE",
  private_url: "URL",
  account_number: "ACCOUNT",
  secret: "SECRET",
};

/** Convenience: raw model label -> display category in one step. */
export function displayCategoryOf(label: string): DisplayCategory | null {
  const internal = mapModelLabel(label);
  return internal ? INTERNAL_TO_DISPLAY[internal] : null;
}

/** Reverse map: user-facing display category -> internal category string. */
export const DISPLAY_TO_INTERNAL: Record<DisplayCategory, InternalCategory> = {
  PERSON: "private_person",
  EMAIL: "private_email",
  PHONE: "private_phone",
  ADDRESS: "private_address",
  DATE: "private_date",
  URL: "private_url",
  ACCOUNT: "account_number",
  SECRET: "secret",
};
